"use client";

import Link from "next/link";
import AnimatedSection from "./AnimatedSection";

/**
 * After-hours section — positioned between Portfolio (paid work) and
 * TechStack (tools). The job of this section: show that the workshop
 * runs past the brief, that craft happens here even when no one is
 * watching. The forest walk is the proof — a Three.js scene built on
 * weekends. The CTA leads there.
 *
 * Workshop/artisan voice: the copy is opinionated and personal
 * ("when there's no brief", "no one paying for it") and the visual
 * treatment leans on texture (paper-grain via the .glass-card overlay
 * already shipped) and a single big serif-feeling headline rather
 * than the dense card grid above.
 */
const AfterHours = () => {
  return (
    <section id="after-hours" className="relative px-4 py-24">
      <div className="mx-auto max-w-5xl">
        <AnimatedSection className="text-center">
          <p className="mb-4 text-sm font-medium uppercase tracking-[0.25em] text-emerald-400/80 font-[family-name:var(--font-jetbrains-mono)]">
            05 <span className="text-emerald-500/50">{"//"}</span> after_hours
          </p>
          <h2 className="font-[family-name:var(--font-space-grotesk)] text-4xl font-bold leading-[1.02] tracking-[-0.02em] [text-wrap:balance] md:text-7xl">
            The workshop <span className="gradient-text">never closes</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-[#94A3B8] leading-relaxed">
            Some of what comes out of here has no client and no deadline. A
            walk through a hand-built forest is one of them — 200 procedural
            trees, god rays, leaves that actually fall like leaves. Press
            through.
          </p>

          {/* CTA — visually distinct from the small floating ForestLink in
              the corner. This is the front-door invitation; the corner
              button is the always-visible reminder. */}
          <AnimatedSection delay={0.1} className="mt-10 inline-block">
            <Link
              href="/forest"
              className="group relative inline-flex items-center gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-950/40 px-7 py-4 text-base font-medium text-emerald-200 backdrop-blur-md transition-all hover:border-emerald-400/70 hover:bg-emerald-900/50 hover:shadow-[0_0_40px_rgba(16,185,129,0.25)]"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                className="text-emerald-400 transition-transform group-hover:rotate-3"
                aria-hidden
              >
                <path d="M12 2L7 9h2.5L6 15h3l-2 5h10l-2-5h3l-3.5-6H17L12 2z" fill="currentColor" />
                <rect x="11" y="20" width="2" height="2" rx="0.5" fill="currentColor" opacity="0.7" />
              </svg>
              Enter the forest
              <span className="text-emerald-400 transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </AnimatedSection>

          {/* Workshop signature — small, hand-attributed. Reinforces
              "one person made this" without shouting it. The italic +
              dim color keeps it humble. */}
          {/* Secondary after-hours destination — the retired hero
              starfield (and its resident UFO) live on at /starfield.
              Deliberately quieter than the forest CTA above. */}
          <p className="mt-5 text-sm">
            <Link
              href="/starfield"
              className="group inline-flex items-center gap-2 text-emerald-300/60 transition-colors hover:text-emerald-200"
            >
              or drift through the deep field
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </p>

          <p className="mt-8 text-xs italic text-[#94A3B8]/60 font-[family-name:var(--font-space-grotesk)]">
            built between client work — Micah
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default AfterHours;
